import {
  ConnectionListResult,
  FeatureFlagsContext,
  GridSortProps,
  PaginatedQueryWrapper
} from '@tableau/data-catalog-shared';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import {
  FlowDownstreamWorkbooksFilteredByFlowOutputFieldQuery,
  FlowDownstreamWorkbooksFilteredByFlowOutputFieldQuery_flows_outputFieldsConnection_nodes_downstreamWorkbooksConnection_nodes,
  FlowDownstreamWorkbooksFilteredByFlowOutputFieldQueryVariables,
} from '../../../gen-src/queries/FlowDownstreamWorkbooksFilteredByFlowOutputFieldQuery';
import {
  FlowDownstreamWorkbooksFilteredByFlowOutputFieldWithoutPersonalSpaceQuery,
  // tslint:disable-next-line: max-line-length
  FlowDownstreamWorkbooksFilteredByFlowOutputFieldWithoutPersonalSpaceQuery_flows_outputFieldsConnection_nodes_downstreamWorkbooksConnection_nodes,
  FlowDownstreamWorkbooksFilteredByFlowOutputFieldWithoutPersonalSpaceQueryVariables,
} from '../../../gen-src/queries/FlowDownstreamWorkbooksFilteredByFlowOutputFieldWithoutPersonalSpaceQuery';
import {
  FlowDownstreamWorkbooksQuery,
  FlowDownstreamWorkbooksQuery_flows_downstreamWorkbooksConnection_nodes,
  FlowDownstreamWorkbooksQueryVariables,
} from '../../../gen-src/queries/FlowDownstreamWorkbooksQuery';
import {
  FlowDownstreamWorkbooksWithoutPersonalSpaceQuery,
  FlowDownstreamWorkbooksWithoutPersonalSpaceQuery_flows_downstreamWorkbooksConnection_nodes,
  FlowDownstreamWorkbooksWithoutPersonalSpaceQueryVariables,
} from '../../../gen-src/queries/FlowDownstreamWorkbooksWithoutPersonalSpaceQuery';
import { WorkbookSortOrder } from '../../../gen-src/queries/globalTypes';
import {
  FlowDownstreamWorkbooksFilteredByFlowOutputFieldQueryGql,
} from '../../queries/FlowDownstreamWorkbooksFilteredByFlowOutputFieldQueryGql';
import { FlowDownstreamWorkbooksFilteredByFlowOutputFieldWithoutPersonalSpaceQueryGql } from '../../queries/FlowDownstreamWorkbooksFilteredByFlowOutputFieldWithoutPersonalSpaceQueryGql';
import { FlowDownstreamWorkbooksQueryGql } from '../../queries/FlowDownstreamWorkbooksQueryGql';
import { FlowDownstreamWorkbooksWithoutPersonalSpaceQueryGql } from '../../queries/FlowDownstreamWorkbooksWithoutPersonalSpaceQueryGql';
import { getRowDataForWorkbook, getRowDataForWorkbookWithoutPersonalSpace, WorkbooksDisplayGrid, WorkbooksDisplayGridRowData } from '../sharedDisplayGrid/WorkbooksDisplayGrid';
import { WorkbooksValidSortFields } from '../sorting/sortProviders/WorkbooksGridSortProvider';

type QueryVariables = FlowDownstreamWorkbooksQueryVariables;
type QueryResults = FlowDownstreamWorkbooksQuery;
type QueryResultListNode = FlowDownstreamWorkbooksQuery_flows_downstreamWorkbooksConnection_nodes;
type GridRowData = WorkbooksDisplayGridRowData;

type QueryVariablesWithoutPersonalSpace = FlowDownstreamWorkbooksWithoutPersonalSpaceQueryVariables;
type QueryResultsWithoutPersonalSpace = FlowDownstreamWorkbooksWithoutPersonalSpaceQuery;
type QueryResultListNodeWithoutPersonalSpace =
  FlowDownstreamWorkbooksWithoutPersonalSpaceQuery_flows_downstreamWorkbooksConnection_nodes;

type QueryVariablesWithFilter = FlowDownstreamWorkbooksFilteredByFlowOutputFieldQueryVariables;
type QueryResultsWithFilter = FlowDownstreamWorkbooksFilteredByFlowOutputFieldQuery;
type QueryResultListNodeWithFilter =
  FlowDownstreamWorkbooksFilteredByFlowOutputFieldQuery_flows_outputFieldsConnection_nodes_downstreamWorkbooksConnection_nodes;

type QueryVariablesWithFilterWithoutPersonalSpace = FlowDownstreamWorkbooksFilteredByFlowOutputFieldWithoutPersonalSpaceQueryVariables;
type QueryResultsWithFilterWithoutPersonalSpace = FlowDownstreamWorkbooksFilteredByFlowOutputFieldWithoutPersonalSpaceQuery;
// tslint:disable-next-line: max-line-length
type QueryResultListNodeWithFilterWithoutPersonalSpace = FlowDownstreamWorkbooksFilteredByFlowOutputFieldWithoutPersonalSpaceQuery_flows_outputFieldsConnection_nodes_downstreamWorkbooksConnection_nodes;


function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  return flow ? flow.downstreamWorkbooksConnection : null;
}

function selectConnectionListResultWithoutPersonalSpace(resultData: QueryResultsWithoutPersonalSpace):
  ConnectionListResult<QueryResultListNodeWithoutPersonalSpace> | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  return flow ? flow.downstreamWorkbooksConnection : null;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNodeWithFilter> | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  const flowOutputFields = flow && flow.outputFieldsConnection && flow.outputFieldsConnection.nodes;
  const field = flowOutputFields && flowOutputFields[0];
  return field ? field.downstreamWorkbooksConnection : null;
}

function selectConnectionListResultForFilteredQueryWithoutPersonalSpace(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNodeWithFilter> | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  const flowOutputFields = flow && flow.outputFieldsConnection && flow.outputFieldsConnection.nodes;
  const field = flowOutputFields && flowOutputFields[0];
  return field ? field.downstreamWorkbooksConnection : null;
}

export type FlowDownstreamWorkbooksQueryGridProps =
  GridSortProps<WorkbooksValidSortFields> & {
    readonly flowVizportalUrlId: string;
    readonly filteredFlowOutputFieldId: string | null;
    readonly rowCount: number | null;
  };

export function FlowDownstreamWorkbooksQueryGrid(props: FlowDownstreamWorkbooksQueryGridProps): JSX.Element {
  const { personalSpaceEnabled } = React.useContext(FeatureFlagsContext);
  const emptyGridMessage = Messages.EmptyDownstreamWorkbooksLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyDownstreamFilteredWorkbooksLineageGridMessage();

  return personalSpaceEnabled ? (
    props.filteredFlowOutputFieldId ? (
      <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNodeWithFilter, GridRowData>
        queryGql={FlowDownstreamWorkbooksFilteredByFlowOutputFieldQueryGql}
        partialQueryVariables={{
          flowVizportalUrlId: props.flowVizportalUrlId,
          selectedFlowOutputFieldId: props.filteredFlowOutputFieldId,
          orderBy: props.orderBy as WorkbookSortOrder,
        }}
        selectConnectionListResult={selectConnectionListResultForFilteredQuery}
        mapResultNode={getRowDataForWorkbook}
      >
        {({ rowStore }) => <WorkbooksDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
      </PaginatedQueryWrapper>
    ) : (
      <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
        queryGql={FlowDownstreamWorkbooksQueryGql}
        partialQueryVariables={{
          flowVizportalUrlId: props.flowVizportalUrlId,
          orderBy: props.orderBy as WorkbookSortOrder,
        }}
        selectConnectionListResult={selectConnectionListResult}
        mapResultNode={getRowDataForWorkbook}
      >
        {({ rowStore }) => <WorkbooksDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
      </PaginatedQueryWrapper>
    )
  ) : (
    props.filteredFlowOutputFieldId ? (
      <PaginatedQueryWrapper<QueryVariablesWithFilterWithoutPersonalSpace, QueryResultsWithFilterWithoutPersonalSpace,
      QueryResultListNodeWithFilterWithoutPersonalSpace, GridRowData>
        queryGql={FlowDownstreamWorkbooksFilteredByFlowOutputFieldWithoutPersonalSpaceQueryGql}
        partialQueryVariables={{
          flowVizportalUrlId: props.flowVizportalUrlId,
          selectedFlowOutputFieldId: props.filteredFlowOutputFieldId,
          orderBy: props.orderBy as WorkbookSortOrder,
        }}
        selectConnectionListResult={selectConnectionListResultForFilteredQueryWithoutPersonalSpace}
        mapResultNode={getRowDataForWorkbookWithoutPersonalSpace}
      >
        {({ rowStore }) => <WorkbooksDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
      </PaginatedQueryWrapper>
    ) : (
      <PaginatedQueryWrapper<QueryVariablesWithoutPersonalSpace, QueryResultsWithoutPersonalSpace,
      QueryResultListNodeWithoutPersonalSpace, GridRowData>
        queryGql={FlowDownstreamWorkbooksWithoutPersonalSpaceQueryGql}
        partialQueryVariables={{
          flowVizportalUrlId: props.flowVizportalUrlId,
          orderBy: props.orderBy as WorkbookSortOrder,
        }}
        selectConnectionListResult={selectConnectionListResultWithoutPersonalSpace}
        mapResultNode={getRowDataForWorkbookWithoutPersonalSpace}
      >
        {({ rowStore }) => <WorkbooksDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
      </PaginatedQueryWrapper>
    )
  );
}
